import { useState, useMemo, useCallback } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Paper,
  TextInput,
  Select,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Center,
  Loader,
  Divider,
  Alert,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconCheck,
  IconX,
  IconFilter,
  IconRefresh,
  IconInfoCircle,
  IconAlertCircle,
} from "@tabler/icons-react";
import { AddPortalGrantModal } from "../../../components/admin/portal-access/AddPortalGrantModal";
import { usePortalAccessGrantsForSubject } from "../../../hooks/admin/usePortalAccess";
import {
  usePhysicianReportDeliveryAdmin,
  useCreatePhysicianReportDelivery,
  useUpdatePhysicianReportDelivery,
  useTogglePhysicianReportDeliveryActive,
} from "../../../hooks/admin/usePhysicianReportDelivery";
import { usePhysiciansAdmin } from "../../../hooks/admin/usePhysicians";
import { useReportDeliveryMethodsAdmin } from "../../../hooks/admin/useReportDeliveryMethods";
import PhysicianReportDeliveryForm, {
  PhysicianReportDeliveryFormValues,
} from "../../../components/admin/physicianreportdelivery/PhysicianReportDeliveryForm";
import { PhysicianReportDelivery } from "../../../services/admin/physicianreportdelivery.service";

interface DeliveryFilters {
  status: "all" | "active" | "inactive";
  physicianId?: string;
  methodId?: string;
}

const ITEMS_PER_PAGE = 20;

export default function PhysicianReportDeliveryAdminPage() {
  // Modal states
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);
  const [
    portalGrantModalOpened,
    { open: openPortalGrantModal, close: closePortalGrantModal },
  ] = useDisclosure(false);
  // Tracks the form's live physician + method selection so the modal can warn
  // about missing portal access the moment Portal is chosen — before any save.
  const [formSelection, setFormSelection] = useState<{
    physicianId: string;
    reportDeliveryMethodId: string;
  }>({ physicianId: "", reportDeliveryMethodId: "" });

  // Page state
  const [editing, setEditing] = useState<PhysicianReportDelivery | null>(null);
  const [togglingItem, setTogglingItem] =
    useState<PhysicianReportDelivery | null>(null);
  const [filters, setFilters] = useState<DeliveryFilters>({ status: "all" });
  const [pendingFilters, setPendingFilters] = useState<DeliveryFilters>({
    status: "all",
  });
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);

  const {
    data: deliveries = [],
    isLoading,
    refetch,
  } = usePhysicianReportDeliveryAdmin();
  const { data: physicians = [] } = usePhysiciansAdmin();
  const { data: deliveryMethods = [] } = useReportDeliveryMethodsAdmin();
  const createDelivery = useCreatePhysicianReportDelivery();
  const updateDelivery = useUpdatePhysicianReportDelivery();
  const toggleActive = useTogglePhysicianReportDeliveryActive();

  // Filter deliveries
  const filteredDeliveries = useMemo(() => {
    let result = deliveries;

    // Apply search filter
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(
        (d) =>
          d.physician?.firstName?.toLowerCase().includes(searchLower) ||
          d.physician?.lastName?.toLowerCase().includes(searchLower) ||
          d.reportDeliveryMethod?.name?.toLowerCase().includes(searchLower) ||
          d.email?.toLowerCase().includes(searchLower) ||
          d.phone?.includes(searchLower) ||
          d.fax?.includes(searchLower)
      );
    }

    // Apply status filter
    if (filters.status !== "all") {
      result = result.filter((d) =>
        filters.status === "active" ? d.isActive : !d.isActive
      );
    }

    // Apply physician filter
    if (filters.physicianId) {
      result = result.filter((d) => d.physicianId === filters.physicianId);
    }

    // Apply delivery method filter
    if (filters.methodId) {
      result = result.filter(
        (d) => d.reportDeliveryMethodId === filters.methodId
      );
    }

    return result;
  }, [deliveries, debouncedSearch, filters]);

  // Pagination
  const totalPages = Math.ceil(filteredDeliveries.length / ITEMS_PER_PAGE);
  const paginatedDeliveries = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredDeliveries.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredDeliveries, page]);

  // Check if any filters are active
  const hasActiveFilters =
    filters.status !== "all" || filters.physicianId || filters.methodId;

  // Filter handlers
  const handleOpenFilterModal = () => {
    setPendingFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...pendingFilters });
    setPage(1);
    closeFilterModal();
  };

  const handleClearAllFilters = () => {
    setFilters({ status: "all" });
    setPendingFilters({ status: "all" });
    setPage(1);
  };

  const handleClearPendingFilters = () => {
    setPendingFilters({ status: "all" });
  };

  const handleAdd = () => {
    setEditing(null);
    setFormSelection({ physicianId: "", reportDeliveryMethodId: "" });
    openFormModal();
  };

  const handleEdit = (item: PhysicianReportDelivery) => {
    setEditing(item);
    setFormSelection({
      physicianId: item.physicianId,
      reportDeliveryMethodId: item.reportDeliveryMethodId,
    });
    openFormModal();
  };

  const handleSelectionChange = useCallback(
    (selection: { physicianId: string; reportDeliveryMethodId: string }) => {
      setFormSelection(selection);
    },
    []
  );

  const handleSubmit = async (values: PhysicianReportDeliveryFormValues) => {
    const normalized = {
      ...values,
      hl7Port: values.hl7Port === "" ? null : values.hl7Port,
      practiceId: values.practiceId || null,
    };
    try {
      if (editing) {
        await updateDelivery.mutateAsync({ id: editing.id, ...normalized });
      } else {
        await createDelivery.mutateAsync(normalized);
      }
      closeFormModal();
    } catch {
      // Portal-without-access is prevented in the UI (see portalNeedsSetup),
      // so submission can't reach the backend's NO_PORTAL_ACCESS guard. All
      // other failures are surfaced by the mutation hook's onError toast.
    }
  };

  const handleCloseFormModal = useCallback(() => {
    closeFormModal();
  }, [closeFormModal]);

  const handleOpenPortalGrantModal = useCallback(() => {
    openPortalGrantModal();
  }, [openPortalGrantModal]);

  const handleClosePortalGrantModal = useCallback(() => {
    closePortalGrantModal();
  }, [closePortalGrantModal]);

  const handleToggleActive = (item: PhysicianReportDelivery) => {
    setTogglingItem(item);
    openConfirmModal();
  };

  const confirmToggleActive = async () => {
    if (!togglingItem) return;
    try {
      await toggleActive.mutateAsync(togglingItem.id);
      closeConfirmModal();
      setTogglingItem(null);
    } catch {
      // Error is handled by the mutation's onError callback
      // Just close the modal
      closeConfirmModal();
      setTogglingItem(null);
    }
  };

  // Prepare dropdown options
  const physicianOptions = physicians
    .filter((p) => p.isActive)
    .map((p) => ({
      value: p.id,
      label: `${p.lastName}, ${p.firstName}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const methodOptions = deliveryMethods
    .filter((m) => m.isActive)
    .map((m) => ({
      value: m.id,
      label: m.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Get labels for active filter badges
  const getPhysicianLabel = (id: string) => {
    const physician = physicians.find((p) => p.id === id);
    return physician ? `${physician.lastName}, ${physician.firstName}` : id;
  };

  const getMethodLabel = (id: string) => {
    const method = deliveryMethods.find((m) => m.id === id);
    return method?.name || id;
  };

  // Portal delivery needs at least one active portal grant on the physician.
  // Resolve the live selection so the modal can warn the moment Portal is
  // chosen and none exists — before any save is attempted.
  const selectedMethodIsPortal = useMemo(() => {
    const method = deliveryMethods.find(
      (m) => m.id === formSelection.reportDeliveryMethodId
    );
    return (method?.name?.toLowerCase() || "").startsWith("portal");
  }, [deliveryMethods, formSelection.reportDeliveryMethodId]);

  const { data: subjectGrants = [], isLoading: grantsLoading } =
    usePortalAccessGrantsForSubject(
      "physician",
      formModalOpened && selectedMethodIsPortal
        ? formSelection.physicianId
        : null
    );

  const portalNeedsSetup =
    selectedMethodIsPortal &&
    !!formSelection.physicianId &&
    !grantsLoading &&
    !subjectGrants.some((g) => g.state === "active" || g.state === "invited");

  const portalIsPending =
    selectedMethodIsPortal &&
    !!formSelection.physicianId &&
    !grantsLoading &&
    subjectGrants.some((g) => g.state === "invited") &&
    !subjectGrants.some((g) => g.state === "active");

  const selectedPhysicianLabel = useMemo(() => {
    const physician = physicians.find(
      (p) => p.id === formSelection.physicianId
    );
    return physician
      ? `${physician.firstName} ${physician.lastName}`
      : "This physician";
  }, [physicians, formSelection.physicianId]);

  // Setting up portal access from the warning always creates the primary
  // grant (the physician's own access, role "Self"), pre-filled from the
  // physician record.
  const portalPrimaryDefaults = useMemo(() => {
    const physician = physicians.find(
      (p) => p.id === formSelection.physicianId
    );
    return {
      email: physician?.email ?? "",
      firstName: physician?.firstName ?? "",
      lastName: physician?.lastName ?? "",
      role: "Self",
    };
  }, [physicians, formSelection.physicianId]);

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Physician Report Delivery</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage report delivery methods and contact information for
                physicians
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add Delivery
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            {/* Search Section */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by physician, method, email, phone, or fax..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            {/* Filters Section */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {filters.status !== "all" && (
                      <Badge variant="light" color="blue">
                        {filters.status === "active" ? "Active" : "Inactive"}
                      </Badge>
                    )}
                    {filters.physicianId && (
                      <Badge variant="light" color="violet">
                        {getPhysicianLabel(filters.physicianId)}
                      </Badge>
                    )}
                    {filters.methodId && (
                      <Badge variant="light" color="cyan">
                        {getMethodLabel(filters.methodId)}
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filteredDeliveries.length} configuration
                  {filteredDeliveries.length !== 1 ? "s" : ""} found
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Table Header */}
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Delivery Configurations ({filteredDeliveries.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(
                        page * ITEMS_PER_PAGE,
                        filteredDeliveries.length
                      )}{" "}
                      of {filteredDeliveries.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {/* Table */}
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredDeliveries.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {debouncedSearch || hasActiveFilters
                    ? "No configurations match the current filters"
                    : "No physician report delivery configurations found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Physician</Table.Th>
                      <Table.Th>Delivery Method</Table.Th>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Phone</Table.Th>
                      <Table.Th>Fax</Table.Th>
                      <Table.Th style={{ width: 100 }}>Status</Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedDeliveries.map((delivery) => (
                      <Table.Tr
                        key={delivery.id}
                        style={{ opacity: delivery.isActive ? 1 : 0.5 }}
                      >
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {delivery.physician
                              ? `${delivery.physician.lastName}, ${delivery.physician.firstName}`
                              : "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {delivery.reportDeliveryMethod?.name || "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{delivery.email || "—"}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{delivery.phone || "—"}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{delivery.fax || "—"}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={delivery.isActive ? "green" : "gray"}
                            variant="light"
                            size="sm"
                          >
                            {delivery.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(delivery)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip
                              label={
                                delivery.isActive ? "Deactivate" : "Activate"
                              }
                            >
                              <ActionIcon
                                variant="subtle"
                                color={delivery.isActive ? "red" : "green"}
                                onClick={() => handleToggleActive(delivery)}
                              >
                                {delivery.isActive ? (
                                  <IconX size={18} />
                                ) : (
                                  <IconCheck size={18} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Add/Edit Modal */}
        <Modal.Root
          opened={formModalOpened}
          onClose={handleCloseFormModal}
          size="md"
          centered
          closeOnClickOutside={false}
          styles={{
            content: {
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
            },
          }}
        >
          <Modal.Overlay />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>
                {editing
                  ? "Edit Physician Report Delivery"
                  : "Add New Physician Report Delivery"}
              </Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body style={{ flex: 1, overflow: "auto" }}>
              <PhysicianReportDeliveryForm
                initialValues={editing || undefined}
                onSubmit={handleSubmit}
                physicians={physicianOptions}
                deliveryMethods={methodOptions}
                onSelectionChange={handleSelectionChange}
                methodWarning={
                  portalNeedsSetup ? (
                    <Alert
                      icon={<IconAlertCircle size={16} />}
                      color="yellow"
                      variant="light"
                      title="Set up portal access first"
                      role="alert"
                    >
                      <Stack gap="sm">
                        <Text size="sm">
                          {selectedPhysicianLabel} has no active or invited
                          portal access grants yet. Add at least one portal
                          grant so the Portal delivery method has a recipient to
                          route to.
                        </Text>
                        <Group>
                          <Button
                            size="xs"
                            variant="filled"
                            color="yellow"
                            onClick={handleOpenPortalGrantModal}
                          >
                            Set up portal access
                          </Button>
                        </Group>
                      </Stack>
                    </Alert>
                  ) : portalIsPending ? (
                    <Alert
                      icon={<IconInfoCircle size={16} />}
                      color="blue"
                      variant="light"
                      title="Portal invitation pending"
                      role="status"
                    >
                      <Text size="sm">
                        {selectedPhysicianLabel} has portal contacts, but none
                        have activated their accounts yet. Reports will be
                        available in the portal once a contact completes
                        registration.
                      </Text>
                    </Alert>
                  ) : null
                }
              />
            </Modal.Body>
            <Divider />
            <Group justify="space-between" p="md">
              <Button variant="default" onClick={handleCloseFormModal}>
                Cancel
              </Button>
              {!portalNeedsSetup && (
                <Button
                  loading={createDelivery.isPending || updateDelivery.isPending}
                  onClick={() => {
                    const form = document.querySelector("form");
                    if (form) form.requestSubmit();
                  }}
                >
                  {editing ? "Update" : "Create"}
                </Button>
              )}
            </Group>
          </Modal.Content>
        </Modal.Root>

        {selectedMethodIsPortal && formSelection.physicianId && (
          <AddPortalGrantModal
            opened={portalGrantModalOpened}
            onClose={handleClosePortalGrantModal}
            subjectType="physician"
            subjectId={formSelection.physicianId}
            subjectName={selectedPhysicianLabel}
            mode="primary"
            primaryDefaults={portalPrimaryDefaults}
          />
        )}

        {/* Filter Modal */}
        <Modal.Root
          opened={filterModalOpened}
          onClose={closeFilterModal}
          size="md"
          centered
          closeOnClickOutside={false}
          styles={{
            content: {
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
            },
          }}
        >
          <Modal.Overlay />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>Filter Delivery Configurations</Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body style={{ flex: 1, overflow: "auto" }}>
              <Stack gap="md">
                <Select
                  label="Status"
                  placeholder="Select status..."
                  data={[
                    { value: "all", label: "All" },
                    { value: "active", label: "Active" },
                    { value: "inactive", label: "Inactive" },
                  ]}
                  value={pendingFilters.status}
                  onChange={(value) =>
                    setPendingFilters({
                      ...pendingFilters,
                      status: (value as "all" | "active" | "inactive") || "all",
                    })
                  }
                />
                <Select
                  label="Physician"
                  placeholder="Select physician..."
                  data={physicianOptions}
                  value={pendingFilters.physicianId || null}
                  onChange={(value) =>
                    setPendingFilters({
                      ...pendingFilters,
                      physicianId: value || undefined,
                    })
                  }
                  searchable
                  clearable
                />
                <Select
                  label="Delivery Method"
                  placeholder="Select delivery method..."
                  data={methodOptions}
                  value={pendingFilters.methodId || null}
                  onChange={(value) =>
                    setPendingFilters({
                      ...pendingFilters,
                      methodId: value || undefined,
                    })
                  }
                  clearable
                />
              </Stack>
            </Modal.Body>
            <Divider />
            <Group justify="space-between" p="md">
              <Button variant="default" onClick={closeFilterModal}>
                Cancel
              </Button>
              <Group gap="xs">
                <Button variant="subtle" onClick={handleClearPendingFilters}>
                  Clear All
                </Button>
                <Button onClick={handleApplyFilters}>Apply Filters</Button>
              </Group>
            </Group>
          </Modal.Content>
        </Modal.Root>

        {/* Confirmation Modal for Activate/Deactivate */}
        <Modal.Root
          opened={confirmModalOpened}
          onClose={() => {
            closeConfirmModal();
            setTogglingItem(null);
          }}
          size="sm"
          centered
        >
          <Modal.Overlay />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>
                {togglingItem?.isActive
                  ? "Deactivate Delivery Configuration"
                  : "Activate Delivery Configuration"}
              </Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body>
              <Text>
                Are you sure you want to{" "}
                <Text span fw={600}>
                  {togglingItem?.isActive ? "deactivate" : "activate"}
                </Text>{" "}
                the{" "}
                <Text span fw={600}>
                  {togglingItem?.reportDeliveryMethod?.name}
                </Text>{" "}
                delivery for{" "}
                <Text span fw={600}>
                  {togglingItem?.physician
                    ? `Dr. ${togglingItem.physician.lastName}, ${togglingItem.physician.firstName}`
                    : "this physician"}
                </Text>
                ?
              </Text>
            </Modal.Body>
            <Divider />
            <Group justify="space-between" p="md">
              <Button
                variant="default"
                onClick={() => {
                  closeConfirmModal();
                  setTogglingItem(null);
                }}
              >
                Cancel
              </Button>
              <Button
                color={togglingItem?.isActive ? "red" : "green"}
                loading={toggleActive.isPending}
                onClick={confirmToggleActive}
              >
                {togglingItem?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Modal.Content>
        </Modal.Root>
      </Box>
    </Container>
  );
}
